import { Date } from '@lsq-frontend/remote-services/all-java/common-mk0';
import {
    BankAccountType,
    InvoiceBuyerApprovalStatus
} from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import { MoneyDto } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/money-dto';
import { EnumType } from 'json-to-graphql-query';

export class EnumInput<T extends string> extends EnumType {
    constructor(value: T) {
        super(value);
    }
}

export interface Query {
    payables?: QueryInput<{ input: PayablesInput }> & PayablesResponseSchema;
    approval?: QueryInput<{ id: number }> & QueryFields<Approval>;
    creditMemoAllocation?: QueryInput<{ id: number }> & QueryFields<CreditMemoAllocation>;
    transfers?: QueryInput<TransfersInput> & TransfersResponse;
    // approvals?: QueryInput<ApprovalsInput> & ApprovalsResponse;
    // creditMemos?: QueryInput<CreditMemosInput> & CreditMemosResponse;
    creditMemo?: QueryInput<{ id: number; companyFilter: CompanyFilterInput }> &
        QueryFields<CreditMemo>;
    supplierUtilizations?: QueryInput<{ input: SupplierUtilizationInput }> &
        SupplierUtilizationSchema;
    businessUnits?: QueryInput<BusinessUnitInput> & BusinessUnitResponse;
    supplierDetail?: QueryInput<{ input: CompanyFilterInput }> & SupplierDetailResponse;
}

export interface QueryInput<I> {
    __args: { [key in keyof I]: any };
}

// This works only for non-nested objects, for now
export type QueryFields<T> = Partial<{ [key in keyof T]: boolean | any }>;

export interface PayablesResponse {
    payables: {
        rows: Payable[];
        totals: PayableTotals;
        totalsByAgeRange: PayableTotals[];
        pagination: PayablesPagination;
    };
}

export interface Payable {
    id: string;
    name: string;
    approvals?: Approval[];
    transfers?: Transfer[];
    creditMemoAllocations?: CreditMemoAllocation[];
    creditMemos?: CreditMemo[];
    relationship?: Relationship;
    isGroup: boolean;
    soleApproval?: Approval;
    buyerExternalID?: string;
    supplierExternalID?: string;
    invoiceDateParent: string;
    termsParent: number;
    amount?: Money;
    status: PayableStatus;
    supplierStatus: PayableSupplierStatus;
    buyerStatus: PayableBuyerStatus;
    statusLegacy: PayableStatusLegacy;
    earlyFundingEligible: boolean;
    canBeRevoked: boolean;
    dueInDays?: number;
    originalDueDate?: string;
    adjustedDueDate?: string;
    payLaterOriginalDueDate?: string;
    payLaterAdjustedDueDate?: string;
    estimatedPayLaterOriginalDueDate?: string;
    estimatedPayLaterAdjustedDueDate?: string;
    actualBuyerDueDate?: string;
    payLaterRequestDate?: string;
    payLaterEligible: boolean;
    termsExtensionDays: number;
    estimatedPayLaterFee?: Money;
    estimatedPayLaterTotal?: Money;
    payLaterFeeRate?: number;
    effectiveFundingDate?: string;
    buyerChargeTotals?: BuyerChargeTotal[];
    buyerFundingChargeTotal?: Money;
    buyerDiscountFee?: Money;
    buyerDiscountFeeRate?: number;
    supplierDiscountFee?: Money;
    supplierDiscountFeeRate?: number;
    fundingRequestDate?: string;
    fundingTransferredAt?: string;
    daysFundedEarly?: number;
    fundingDate?: string;
    earlyPaymentDate?: string;
    fundingAmount?: Money;
    amountLessSupplierDiscountFee?: Money;
    nameSearchMatchedNested: boolean;
    feesDue: Money;
    ineligibilityReason?: string;
}

export interface PayableTotals {
    payableCount: number;
    invoiceCount: number;
    payableAmount: Money;
    uncollectedFundedPayableCount: number;
    uncollectedFundingAmount: Money;
    uncollectedFundingAmountNotYetDue: Money;
    uncollectedFundingAmountDue: Money;
    uncollectedEarlyPaidPayableCount: number;
    uncollectedPayLaterPayableCount: number;
    earlyFundingEligiblePayableCount: number;
    earlyFundingEligibleAmount: Money;
    openPayableCount: number;
    supplierOpenPayableCount: number;
    openAmount: Money;
    supplierOpenAmount: Money;
    openIneligiblePayableCount: number;
    supplierOpenIneligiblePayableCount: number;
    openIneligibleAmount: Money;
    supplierOpenIneligibleAmount: Money;
    openEligiblePayableCount: number;
    supplierOpenEligiblePayableCount: number;
    openEligibleAmount: Money;
    supplierOpenEligibleAmount: Money;
    openAmountCurrent: Money;
    supplierOpenAmountCurrent: Money;
    supplierOpenEligibleAmountCurrent: Money;
    openAmountDayOneToThirty: Money;
    supplierOpenAmountDayOneToThirty: Money;
    supplierOpenEligibleAmountDayOneToThirty: Money;
    openAmountDayThirtyOneToSixty: Money;
    supplierOpenAmountDayThirtyOneToSixty: Money;
    supplierOpenEligibleAmountDayThirtyOneToSixty: Money;
    openAmountDaySixtyOnePlus: Money;
    supplierOpenAmountDaySixtyOnePlus: Money;
    supplierOpenEligibleAmountDaySixtyOnePlus: Money;
    discountFees: Money;
    lateFees: Money;
    ageDaysRangeStart?: number;
    ageDaysRangeEnd?: number;
    relationship?: Relationship;
}

export interface PayablesPagination {
    skip: number;
    take: number;
    sortColumn: PayablesSortColumn;
    sortDirection: SortDirection;
    secondarySortColumn: PayablesSortColumn;
    secondarySortDirection: SortDirection;
}

export interface BusinessUnitPagination {
    skip: number;
    take: number;
    sortColumn: BusinessUnitSortColumn;
    sortDirection: EnumInput<SortDirection>;
    secondarySortColumn: BusinessUnitSortColumn;
    secondarySortDirection: EnumInput<SortDirection>;
    tertiarySortColumn: BusinessUnitSortColumn;
    tertiarySortDirection: EnumInput<SortDirection>;
}

export interface PayablesInput {
    payableIds?: number[];
    companyFilter?: CompanyFilterInput;
    statuses?: EnumInput<PayableStatus>[];
    legacyStatuses?: EnumInput<PayableStatusLegacy>[];
    payableNameSearch?: string;
    includePayablesWithoutOffers?: boolean;
    pendingCreditMemosOnly?: boolean;
    dueDateRange?: {
        startDate: string;
        endDate: string;
    };
    actualBuyerDueDateRange?: {
        startDate: string;
        endDate: string;
    };
    payLaterEligibleOnly?: boolean;
    isCurrentBalanceExport?: boolean;
    pagination?: PayablesPaginationInput;
    includePendingPayables?: boolean;
}

export interface PayablesResponseSchema {
    rows?: QueryFields<Payable>;
    pagination?: QueryFields<PayablesPagination>;
    totals?: QueryFields<PayableTotals>;
}

export enum PayablesSortColumn {
    NAME = 'NAME',
    BUYER_NAME = 'BUYER_NAME',
    SUPPLIER_NAME = 'SUPPLIER_NAME',
    INVOICE_DATE = 'INVOICE_DATE',
    DUE_DATE = 'DUE_DATE',
    DUE_IN = 'DUE_IN',
    AMOUNT = 'AMOUNT',
    EARLY_PAYMENT_DATE = 'EARLY_PAYMENT_DATE',
    FUNDING_DATE = 'FUNDING_DATE',
    EARLY_PAYMENT_AMOUNT = 'EARLY_PAYMENT_AMOUNT',
    BUYER_DISCOUNT_FEE = 'BUYER_DISCOUNT_FEE',
    BUYER_DISCOUNT_FEE_RATE = 'BUYER_DISCOUNT_FEE_RATE',
    SUPPLIER_DISCOUNT_FEE = 'SUPPLIER_DISCOUNT_FEE',
    SUPPLIER_DISCOUNT_FEE_RATE = 'SUPPLIER_DISCOUNT_FEE_RATE',
    STATUS = 'STATUS',
    STATUS_LEGACY = 'STATUS_LEGACY',
    PAY_LATER_FEE = 'PAY_LATER_FEE',
    PAY_LATER_DUE_DATE = 'PAY_LATER_DUE_DATE',
    ESTIMATED_PAY_LATER_DUE_DATE = 'ESTIMATED_PAY_LATER_DUE_DATE',
    PAY_LATER_ELIGIBLE = 'PAY_LATER_ELIGIBLE',
    PAYABLE_TERM_EXTENSION_DAYS = 'TERMS_EXTENSION_DAYS'
}

export enum SupplierUtilizationSortColumn {
    SELLER_ID = 'SELLER_ID',
    NAME = 'NAME',
    EARLY_PAID_AMOUNT = 'EARLY_PAID_AMOUNT',
    OPEN_INVOICE_AMOUNT = 'OPEN_INVOICE_AMOUNT',
    PERCENT_USED = 'PERCENT_USED',
    ENROLLMENT_STATUS = 'ENROLLMENT_STATUS',
    BUYER_NAME = 'BUYER_NAME',
    SELLER_KEY = 'SELLER_KEY',
    BUYER_KEY = 'BUYER_KEY'
}

export enum SortDirection {
    ASCENDING = 'ASCENDING',
    DESCENDING = 'DESCENDING'
}

export interface PayablesPaginationInput {
    skip: number;
    take: number;
    sortColumn: EnumInput<PayablesSortColumn>;
    sortDirection: EnumInput<SortDirection>;
    secondarySortColumn?: EnumInput<PayablesSortColumn>;
    secondarySortDirection?: EnumInput<SortDirection>;
}

export interface SupplierUtilizationPaginationInput {
    Skip: number;
    Take: number;
    sortColumn: EnumInput<PayablesSortColumn>;
    sortDirection: EnumInput<SortDirection>;
    secondarySortColumn: EnumInput<PayablesSortColumn>;
    secondarySortDirection: EnumInput<SortDirection>;
}

export interface BuyerChargeTotal {
    reason: ChargeReason;
    totalAmount: Money;
}

export enum ChargeReason {
    LATE_FEE = 'LATE_FEE',
    PAY_LATER_FEE = 'PAY_LATER_FEE',
    PAY_LATER_LATE_FEE = 'PAY_LATER_LATE_FEE'
}

export interface CompanyFilterInput {
    buyerIds?: string[];
    buyerAccountIds?: string[];
    supplierIds?: string[];
}

export interface ApprovalResponse {
    approval: Approval;
}

export interface Approval {
    id: string;
    payableId: number;
    payableName: string;
    payLaterRequested: boolean;
    amount?: Money;
    adjustedAmount?: Money;
    date: string;
    effectiveFundingDate?: string;
    originalDueDate?: string;
    adjustedDueDate?: string;
    payLaterOriginalDueDate?: string;
    payLaterAdjustedDueDate?: string;
    estimatedPayLaterAdjustedDueDate?: string;
    estimatedPayLaterFee?: Money;
    estimatedPayLaterTotal?: Money;
    amountLessSupplierDiscountFee?: Money;
    invoice?: Invoice;
    creditMemoAllocations?: CreditMemoAllocation[];
    status: PayableStatus;
    statusLegacy: PayableStatusLegacy;
    buyerStatus: PayableBuyerStatus;
    supplierStatus: PayableSupplierStatus;
    canBeRevoked: boolean;
    supplierDiscountFee?: Money;
}

export interface Invoice {
    id: string;
    invoiceNumber: string;
    externalPrimaryKey: string;
    amount: Money;
    relationship: Relationship;
    date: string;
    terms: number;
}

export interface Relationship {
    id: string;
    buyer: Buyer;
    supplier: Supplier;
    scfConfig: ScfConfig;
}

export interface ScfConfig {
    ineligibleDaysBeforeTerms: number;
    isFboManageAllPayments: boolean;
    isOnTermsFundingEnabled: boolean;
    isEarlyFundingEnabled: boolean;
    calculateUsedCreditForBuyer: boolean;
    isBuyerPaidEarlyPaymentFeePercentageVisible: boolean;
    buyerPaidEarlyPaymentFeePercentage: number;
    allPaymentsOptIn: boolean;
    allPaymentsOptInTerms: number;
}

export interface Buyer {
    id: string;
    company: Company;
}

export interface Supplier {
    id: string;
    company: Company;
}

export interface Company {
    id: string;
    name: string;
    legalName: string;
}

export interface CreditMemoAllocationResponse {
    creditMemoAllocation: CreditMemoAllocation;
}

export interface CreditMemoAllocationResponseSchema {}

export interface CreditMemoAllocation {
    id: string;
    approvalId: string;
    allocatedAmount: Money;
    creditMemoId: string;
    creditMemo: CreditMemo;
    payableId: string;
    payableName: string;
    allocationtype: string;
}

export interface CreditMemoResponse {
    creditMemo: CreditMemo;
}

export interface CreditMemo {
    id: string;
    payableName?: string;
    creditMemoNumber?: string;
    date: string;
    amount?: Money;
    allocatedAmount?: Money;
    relationship?: Relationship;
    canBeRevoked: boolean;
}

export interface Money {
    cents: number;
    currencyCode: CurrencyCode;
}

export interface TransfersInput {
    transferIds?: string[];
    companyFilter?: CompanyFilterInput;
    amountOrTraceNumberSearch?: string;
    transferTypes?: TransferType[];
    transferTypesForSupplier?: EnumInput<TransferTypeForSupplier>[];
    statuses?: EnumInput<TransferStatus>[];
    payer?: EnumInput<TransferPayerInput>[];
    pagination?: TransfersPaginationInput;
    dateRange?: {
        startDate: string;
        endDate: string;
    };
}

export enum TransferPayerInput {
    LSQ = 'LSQ',
    BUYER = 'BUYER'
}

export interface TransfersPaginationInput {
    skip?: number;
    take?: number;
    sortColumn?: EnumInput<TransfersSortColumn>;
    sortDirection?: EnumInput<SortDirection>;
}

export interface TransfersPagination {
    skip?: number;
    take?: number;
    sortColumn?: TransfersSortColumn;
    sortDirection?: SortDirection;
}

export interface TransferTotals {
    transferCount: number;
    invoiceCount: number;
    transferAmount: Money;
    fundingAmount: Money;
    discountFees: Money;
    fundingAmountNotYetDue: Money;
    fundingAmountDue: Money;
    feesDue: Money;
    totalAmountDue: Money;
    totalAmountOpen: Money;
    totalAmountNotYetDue: Money;
}

export interface TransfersResponse {
    transfers: {
        rows: Transfer[];
        totals: TransferTotals;
        pagination?: TransfersPagination;
    };
}

export enum BusinessUnitSortColumn {
    BUSINESS_UNIT_NAME = 'BUSINESS_UNIT_NAME',
    BUYER_EXTERNAL_ID = 'BUYER_EXTERNAL_ID',
    SUPPLIER_COUNT = 'SUPPLIER_COUNT',
    OPEN_INVOICE_COUNT = 'OPEN_INVOICE_COUNT',
    EARLY_PAID_AMOUNT = 'EARLY_PAID_AMOUNT',
    AMOUNT_DUE = 'AMOUNT_DUE',
    USED_PERCENTAGE = 'USED_PERCENTAGE',
    TOTAL_BALANCE_AMOUNT = 'TOTAL_BALANCE_AMOUNT',
    OPEN_INVOICE_AMOUNT = 'OPEN_INVOICE_AMOUNT'
}

export enum TransfersSortColumn {
    DATE = 'DATE',
    TRACE_NUMBER = 'TRACE_NUMBER',
    PAYER_NAME = 'PAYER_NAME',
    RECIPIENT_TYPE = 'RECIPIENT_TYPE',
    TYPE = 'TYPE',
    PAYABLE_COUNT = 'PAYABLE_COUNT',
    AMOUNT = 'AMOUNT',
    APPROVED_INVOICE_AMOUNT = 'APPROVED_INVOICE_AMOUNT',
    TOTAL_DISCOUNT_FEE = 'TOTAL_DISCOUNT_FEE',
    BUYER_DISCOUNT_FEE = 'BUYER_DISCOUNT_FEE',
    BUYER_DISCOUNT_FEE_RATE = 'BUYER_DISCOUNT_FEE_RATE',
    SUPPLIER_DISCOUNT_FEE = 'SUPPLIER_DISCOUNT_FEE',
    SUPPLIER_DISCOUNT_FEE_RATE = 'SUPPLIER_DISCOUNT_FEE_RATE',
    BUYER_TRANSACTION_FEE = 'BUYER_TRANSACTION_FEE',
    BUYER_TRANSACTION_FEE_RATE = 'BUYER_TRANSACTION_FEE_RATE',
    STATUS = 'STATUS',
    PAYMENT_DATE = 'PAYMENT_DATE'
}

export enum TransferDetailSortColumn {
    INVOICE_NUMBER = 'INVOICE_NUMBER',
    INVOICE_DATE = 'INVOICE_DATE',
    PAYER_NAME = 'PAYER_NAME',
    PAYEE_NAME = 'PAYEE_NAME',
    INVOICE_AMOUNT = 'INVOICE_AMOUNT',
    TYPE = 'TYPE',
    RECIPIENT = 'RECIPIENT',
    EARLY_PAYMENT_DATE = 'EARLY_PAYMENT_DATE',
    PAYMENT_DATE = 'PAYMENT_DATE',
    PAY_LATER_DATE = 'PAY_LATER_DATE',
    AMOUNT = 'AMOUNT',
    BUYER_TRANSACTION_FEE = 'BUYER_TRANSACTION_FEE',
    BUYER_TRANSACTION_PAID_FEES = 'BUYER_TRANSACTION_PAID_FEES'
}

export enum PaymentDetailType {
    EARLY_PAID = 'EARLY_PAID',
    // FIXME: This should be PAID_ON_TERMS, not PAY_LATER.
    // buyerPaymentDetailType remains EARLY_PAID for an EP that is requested for PL.
    PAY_LATER = 'PAY_LATER',
    PAID_AT_TERMS = 'PAID_AT_TERMS'
}

export interface TransferDetail {
    payableId?: number;
    payableName?: string;
    invoiceId?: number;
    invoiceNumber?: string;
    invoiceDate?: string;
    invoiceAmount?: Money;
    paymentAmount?: Money;
    supplierPaymentAmount?: Money;
    buyerPaymentAmount?: Money;
    paymentDate?: string;
    recipient?: string;
    supplier?: string;
    supplierId?: string;
    buyerFundingChargeTotal?: Money;
    buyerFundingChargePaidTotal?: Money;
    earlyPaymentDate: string;
    onTermsPaymentDate: string;
    buyer?: string;
    supplierKey?: string;
    transferType?: TransferType;
    supplierDiscountFee?: Money;
    buyerDiscountFee?: Money;
    originalDueDate?: string;
    adjustedDueDate: string;
    payLaterAdjustedDueDate: string;
    fundingDueDate: string;
    isGroup?: boolean;
    groupId?: string;
    dueInDays: number;
    statusLegacy?: PayableStatusLegacy;
    buyerPaymentDetailType?: PaymentDetailType;
    isPayLater?: boolean;
    approvals?: Approval[];
    creditMemos?: CreditMemo[];
    numberOfDaysPaidEarly?: number;
}

export interface TransferDetailPaginationInput extends TransfersPaginationInput {
    sortColumn?: EnumInput<TransferDetailSortColumn>;
}

export interface TransferDetailInput {
    transferId?: string;
    companyFilter?: CompanyFilterInput;
    invoiceNumberSearch?: string;
    suppliers?: string[];
    pagination?: TransferDetailPaginationInput;
    payableId?: string;
}

export interface TransferDetailResponse {
    transferDetail: {
        rows: TransferDetail[];
        transferSummary: Transfer;
    };
}

export interface Transfer {
    id: string;
    traceNumber: string;
    initiatedAt?: string;
    transferredAt?: string;
    transferDate: string;
    transferType: TransferType;
    transferTypeForSupplier: TransferTypeForSupplier;
    paymentAmount: Money;
    supplierPaymentAmount: Money;
    buyerPaymentAmount: Money;
    totalApprovedInvoiceAmount: Money;
    fundingAmount?: Money;
    invoiceCount: number;
    totalFundingAndTransactionFees?: Money;
    totalDiscountFee?: Money;
    buyerDiscountFee?: Money;
    supplierDiscountFee?: Money;
    totalTransactionFee?: Money;
    buyerTransactionFee?: Money;
    supplierTransactionFee?: Money;
    payerName?: String;
    recipientType?: String;
    payeeBankAccount?: BankAccount;
    payerBankAccount?: BankAccount;
    status: TransferStatus;
    relationship: Relationship;
    recipientBreakdown: RecipientBreakdown[];
    approvals: Approval[];
    buyerAmountPaidToLsq: Money;
    buyerAmountPaidToSupplier: Money;
    payeeBankAccountType?: BankAccountType;
    payeePaymentMethod?: String;
    bankAccount?: BankAccount;
}

export enum TransferType {
    EARLY_PAYMENT = 'EARLY_PAYMENT',
    ON_TERM_PAYMENT = 'ON_TERM_PAYMENT',
    BUYER_TO_CREDITOR = 'BUYER_TO_CREDITOR',
    REV_SHARE = 'REV_SHARE',
    OVER_PAYMENT = 'OVER_PAYMENT',
    BUYER_TO_FBO = 'BUYER_TO_FBO',
    FBO_TO_CREDITOR = 'FBO_TO_CREDITOR',
    FUNDER_TO_FBO = 'FUNDER_TO_FBO',
    BUYER_TO_FUNDER = 'BUYER_TO_FUNDER'
}

export enum TransferStatus {
    SCHEDULED = 'SCHEDULED',
    INITIATED = 'INITIATED',
    REJECTED = 'REJECTED',
    COMPLETED = 'COMPLETED'
}

export enum TransferTypeForSupplier {
    SCHEDULED_EARLY_PAYMENT = 'SCHEDULED_EARLY_PAYMENT',
    EARLY_PAYMENT = 'EARLY_PAYMENT',
    PAYMENT = 'PAYMENT',
    PENDING = 'PENDING',
    REJECTED = 'REJECTED'
}

export interface BankAccount {
    bankName: string;
    accountNumber: string;
    routingNumber: string;
    wireRoutingNumber: string;
    originId: string;
    cashProOriginId: string;
    entityType: string;
    accountType: string;
}

export interface RecipientBreakdown {
    traceNumber: string;
    transferDate: string;
    payerName?: string;
    recipient?: string;
    recipientType?: string;
    amount: Money;
    totalApprovedInvoiceAmount: Money;
    invoiceCount: number;
    approvals: Approval[];
}

export enum PayableStatusLegacy {
    ELIGIBLE = 'ELIGIBLE',
    INELIGIBLE = 'INELIGIBLE',
    EARLY_PAYMENT_SCHEDULED = 'EARLY_PAYMENT_SCHEDULED',
    PENDING = 'PENDING',
    EARLY_PAID = 'EARLY_PAID',
    PAY_LATER = 'PAY_LATER',
    PAID = 'PAID',
    FUNDING_REJECTED = 'FUNDING_REJECTED',
    REVOKED = 'REVOKED',
    EARLY_PAID_CLOSED = 'EARLY_PAID_CLOSED',
    PAY_LATER_CLOSED = 'PAY_LATER_CLOSED',
    CLOSED = 'CLOSED'
}

export enum PayableStatus {
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED',
    CLOSED = 'CLOSED',
    EARLY_PAY_REQUESTED = 'EARLY_PAY_REQUESTED',
    EARLY_PAID = 'EARLY_PAID',
    EARLY_PAID_CLOSED = 'EARLY_PAID_CLOSED',
    EARLY_PAID_PAY_LATER = 'EARLY_PAID_PAY_LATER',
    EARLY_PAID_PAY_LATER_CLOSED = 'EARLY_PAID_PAY_LATER_CLOSED',
    PAY_LATER = 'PAY_LATER',
    PAY_LATER_REQUESTED = 'PAY_LATER_REQUESTED',
    PAY_LATER_CLOSED = 'PAY_LATER_CLOSED'
}

export enum PayableBuyerStatus {
    REVOKED = 'REVOKED',
    CLOSED = 'CLOSED',
    PENDING = 'PENDING',
    EARLY_PAID = 'EARLY_PAID',
    PAY_LATER = 'PAY_LATER',
    ELIGIBLE = 'ELIGIBLE',
    INELIGIBLE = 'INELIGIBLE'
}

export enum PayableSupplierStatus {
    PAID = 'PAID',
    PENDING = 'PENDING',
    EARLY_PAID = 'EARLY_PAID',
    ELIGIBLE = 'ELIGIBLE',
    INELIGIBLE = 'INELIGIBLE'
}

export interface SupplierUtilizationSchema {
    rows?: QueryFields<SupplierUtilization>;
    totals?: QueryFields<SupplierUtilizationTotals>;
}

export interface SupplierUtilizationResponse {
    rows: SupplierUtilization[];
    totals: SupplierUtilizationTotals;
}

export enum SupplierSummaryEnrollmentStatus {
    UNKNOWN = 'UNKNOWN',
    ENROLLED = 'ENROLLED',
    NOT_ENROLLED = 'NOT_ENROLLED',
    INELIGIBLE = 'INELIGIBLE'
}

export interface SupplierUtilizationTotals {
    supplierCount: number;
    enrolledSuppliers: number;
    unenrolledSuppliers: number;
}

export interface SupplierUtilizationPaginationInput {
    skip?: number;
    take?: number;
}

export interface SupplierUtilizationInput {
    companyFilter?: CompanyFilterInput;
    excludeZeroEarlyPay?: boolean;
    sellerNameFilter?: string;
    sellerEnrollmentStatusFilter?: EnumInput<SupplierSummaryEnrollmentStatus>[];
    pagination?: SupplierUtilizationPaginationInput;
    buyerPaidEarlyPaymentFeePercentageVisible?: boolean;
    forCurrentWeek?: boolean;
}

export interface SupplierUtilization {
    sellerSummaryKey?: string;
    name?: string;
    earlyPaidAmount?: Money;
    flexTermAmount?: Money;
    enrollmentStatus?: SupplierSummaryEnrollmentStatus;
    openInvoiceAmount?: Money;
    feeShare?: number;
    percentUsed?: number;
    flexTermPercentUsed?: number;
    earlyPaidPercentUsed?: number;
    sellerId?: string;
    buyerName?: string;
    sellerKey?: string;
    buyerKey?: string;
    totalPaidFees?: Money;
    buyerPaidEarlyPaymentFeePercentageVisible: boolean;
}

export enum CurrencyCode {
    UNKNOWN_CURRENCY_CODE = 'UNKNOWN_CURRENCY_CODE',
    AED = 'AED',
    AFN = 'AFN',
    ALL = 'ALL',
    AMD = 'AMD',
    ANG = 'ANG',
    AOA = 'AOA',
    ARS = 'ARS',
    AUD = 'AUD',
    AWG = 'AWG',
    AZN = 'AZN',
    BAM = 'BAM',
    BBD = 'BBD',
    BDT = 'BDT',
    BGN = 'BGN',
    BHD = 'BHD',
    BIF = 'BIF',
    BMD = 'BMD',
    BND = 'BND',
    BOB = 'BOB',
    BOV = 'BOV',
    BRL = 'BRL',
    BSD = 'BSD',
    BTN = 'BTN',
    BWP = 'BWP',
    BYR = 'BYR',
    BZD = 'BZD',
    CAD = 'CAD',
    CDF = 'CDF',
    CHE = 'CHE',
    CHF = 'CHF',
    CHW = 'CHW',
    CLF = 'CLF',
    CLP = 'CLP',
    CNY = 'CNY',
    COP = 'COP',
    COU = 'COU',
    CRC = 'CRC',
    CUC = 'CUC',
    CUP = 'CUP',
    CVE = 'CVE',
    CZK = 'CZK',
    DJF = 'DJF',
    DKK = 'DKK',
    DOP = 'DOP',
    DZD = 'DZD',
    EGP = 'EGP',
    ERN = 'ERN',
    ETB = 'ETB',
    EUR = 'EUR',
    FJD = 'FJD',
    FKP = 'FKP',
    GBP = 'GBP',
    GEL = 'GEL',
    GHS = 'GHS',
    GIP = 'GIP',
    GMD = 'GMD',
    GNF = 'GNF',
    GTQ = 'GTQ',
    GYD = 'GYD',
    HKD = 'HKD',
    HNL = 'HNL',
    HRK = 'HRK',
    HTG = 'HTG',
    HUF = 'HUF',
    IDR = 'IDR',
    ILS = 'ILS',
    INR = 'INR',
    IQD = 'IQD',
    IRR = 'IRR',
    ISK = 'ISK',
    JMD = 'JMD',
    JOD = 'JOD',
    JPY = 'JPY',
    KES = 'KES',
    KGS = 'KGS',
    KHR = 'KHR',
    KMF = 'KMF',
    KPW = 'KPW',
    KRW = 'KRW',
    KWD = 'KWD',
    KYD = 'KYD',
    KZT = 'KZT',
    LAK = 'LAK',
    LBP = 'LBP',
    LKR = 'LKR',
    LRD = 'LRD',
    LSL = 'LSL',
    LYD = 'LYD',
    MAD = 'MAD',
    MDL = 'MDL',
    MGA = 'MGA',
    MKD = 'MKD',
    MMK = 'MMK',
    MNT = 'MNT',
    MOP = 'MOP',
    MRU = 'MRU',
    MUR = 'MUR',
    MVR = 'MVR',
    MWK = 'MWK',
    MXN = 'MXN',
    MXV = 'MXV',
    MYR = 'MYR',
    MZN = 'MZN',
    NAD = 'NAD',
    NGN = 'NGN',
    NIO = 'NIO',
    NOK = 'NOK',
    NPR = 'NPR',
    NZD = 'NZD',
    OMR = 'OMR',
    PAB = 'PAB',
    PEN = 'PEN',
    PGK = 'PGK',
    PHP = 'PHP',
    PKR = 'PKR',
    PLN = 'PLN',
    PYG = 'PYG',
    QAR = 'QAR',
    RON = 'RON',
    RSD = 'RSD',
    RUB = 'RUB',
    RWF = 'RWF',
    SAR = 'SAR',
    SBD = 'SBD',
    SCR = 'SCR',
    SDG = 'SDG',
    SEK = 'SEK',
    SGD = 'SGD',
    SHP = 'SHP',
    SLL = 'SLL',
    SOS = 'SOS',
    SRD = 'SRD',
    SSP = 'SSP',
    STN = 'STN',
    SVC = 'SVC',
    SYP = 'SYP',
    SZL = 'SZL',
    THB = 'THB',
    TJS = 'TJS',
    TMT = 'TMT',
    TND = 'TND',
    TOP = 'TOP',
    TRY = 'TRY',
    TTD = 'TTD',
    TWD = 'TWD',
    TZS = 'TZS',
    UAH = 'UAH',
    UGX = 'UGX',
    USD = 'USD',
    USN = 'USN',
    UYI = 'UYI',
    UYU = 'UYU',
    UZS = 'UZS',
    VEF = 'VEF',
    VND = 'VND',
    VUV = 'VUV',
    WST = 'WST',
    XAF = 'XAF',
    XCD = 'XCD',
    XDR = 'XDR',
    XOF = 'XOF',
    XPF = 'XPF',
    XSU = 'XSU',
    XUA = 'XUA',
    YER = 'YER',
    ZAR = 'ZAR',
    ZMW = 'ZMW',
    ZWL = 'ZWL'
}

export interface FacilityUtilizationInput {
    companyFilter: CompanyFilterInput;
}

export interface FacilityUtilization {
    limit: Money;
    utilized: Money;
    available: Money;
}

export interface FacilityUtilizationResponse {
    facilityUtilization: {
        payLater: FacilityUtilization;
        default: FacilityUtilization;
    };
}
export interface BusinessUnitInput {
    buyerKeys?: string[];
    businessUnitNameFilter?: string;
    pagination?: BusinessUnitPagination;
}
export interface BusinessUnit {
    buyerId: string;
    buyerKey: string;
    buyerCompanyName: string;
    supplierCount: number;
    relationshipKey: string;
    openInvoiceCount: number;
    openInvoiceAmount: Money;
    earlyPaidAmount: Money;
    usedPercentage: number;
    amountDue: Money;
    totalBalanceAmount: Money;
    limit: Money;
    utilized: Money;
    available: Money;
    scfConfig: ScfConfig;
}

export interface BusinessUnitResponse {
    businessUnits: {
        rows: BusinessUnit[];
        totals: {
            totalCount: number;
        };
    };
}

export enum InvoiceFundingStatus2 {
    UNFUNDED = 'UNFUNDED',
    ELIGIBLE = 'ELIGIBLE',
    SCHEDULED = 'SCHEDULED',
    PENDING = 'PENDING',
    FUNDED = 'FUNDED',
    EARLY_PAID = 'EARLY_PAID',
    CLOSED = 'CLOSED',
    WILL_NOT_FUND = 'WILL_NOT_FUND',
    INELIGIBLE = 'INELIGIBLE',
    UNKNOWN_STATUS = 'UNKNOWN_STATUS',
    SENT = 'SENT',
    FUNDING_REJECTED = 'FUNDING_REJECTED',
    EARLY_PAID_CLOSED = 'EARLY_PAID_CLOSED',
    PAID = 'PAID',
    REVOKED = 'REVOKED',
    PAY_LATER = 'PAY_LATER',
    CREDIT_MEMO = 'CREDIT_MEMO'
}

export enum EarlyPaymentStatus2 {
    UNKNOWN_PAYMENT_STATUS = 'UNKNOWN_PAYMENT_STATUS',
    EP_SCHEDULED = 'EP_SCHEDULED',
    EP_PENDING = 'EP_PENDING',
    EP_REJECTED = 'EP_REJECTED',
    EP_FUNDED = 'EP_FUNDED',
    EP_FUNDED_TERMS = 'EP_FUNDED_TERMS',
    EP_FUNDED_TERMS_PAY_LATER = 'EP_FUNDED_TERMS_PAY_LATER'
}

export enum BuyerInvoiceStatus2 {
    UNKNOWN_STATUS = 'UNKNOWN_STATUS',
    ELIGIBLE = 'ELIGIBLE',
    EARLY_PAID = 'EARLY_PAID',
    EARLY_PAID_CLOSED = 'EARLY_PAID_CLOSED',
    CLOSED = 'CLOSED',
    INELIGIBLE = 'INELIGIBLE',
    PENDING = 'PENDING',
    REVOKED = 'REVOKED',
    PAY_LATER = 'PAY_LATER',
    PAY_LATER_CLOSED = 'PAY_LATER_CLOSED'
}

export class InvoiceSummary2 {
    public refInvoiceKey: string;
    public invoiceDate: object;
    public buyerApprovalStatus: InvoiceBuyerApprovalStatus;
    public buyerName: string;
    public invoiceNumber: string;
    public invoiceDueDate: object;
    public advanceRate: number;
    public invoiceAmount: Money;
    public finalApprovedAmount: Money;
    public discountAmount: Money;
    public availableAmount: Money;
    public fundingDate: object;
    public fundingDateNew: Date;
    public earlyPaymentDate: object;
    public earlyPaymentDateNew: Date;
    public fundingStatus: InvoiceFundingStatus2;
    public dueIn: number;
    public feeRateHundredthOfBps: number;
    public isGroup: boolean;
    public refGroupedInvoiceKey: string;
    public groupId: string;
    public sellerName: string;
    public groupedInvoiceNumberMatched: boolean;
    public refBuyerKey: string;
    public totalGroupedInvoiceCount: number;
    public invoiceDateNew: Date;
    public invoiceDueDateNew: Date;
    public earlyPaymentStatus: EarlyPaymentStatus2;
    public canBeRevoked: boolean;
    public invoiceStatus: InvoiceFundingStatus2;
    public buyerDiscountAmount: Money;
    public supplierDiscountAmount: Money;
    public buyerDiscountPercentage: number;
    public buyerPaidTransactionFee: boolean;
    public bankAccountType: BankAccountType;
    public paymentMethod: string;
    public sellerKey: string;
    public invoiceFees?: Money;
    public payLaterDueDate?: object;
    public estimatedPayLaterDueDate?: object;
    public payLaterEligible: boolean;
    public payLaterFee: Money;
    public payLaterFeeRate: number;
    public termsExtensionDays: number;
    public estimatedPayLaterTotal: Money;
    public ineligibilityReason: string;

    public constructor(init?: Partial<InvoiceSummary2>) {
        Object.keys(init ?? {}).forEach((k) => (this[k] = init[k]));
    }
}

export interface BuyerPaymentsDetailItem2 {
    batchKey?: string;
    groupId?: string;
    groupedChildSearchMatch?: boolean;
    invoiceAmount?: MoneyDto;
    invoiceDate?: string;
    invoiceKey?: string;
    invoiceNumber?: string;
    isGroup?: boolean;
    numberDaysPaidEarly?: number;
    paymentAmount?: MoneyDto;
    paymentDate?: string;
    paymentType?:
        | 'EARLY_PAYMENT'
        | 'EARLY_PAY_REMITTANCE'
        | 'INVOICE_TERM'
        | 'UNKNOWN_PAYMENT_TYPE';
    supplierKey?: string;
    supplierLegalName?: string;
    buyerFundingChargeTotal?: MoneyDto;
    buyerFundingChargePaidTotal?: MoneyDto;
    earlyPaymentDate?: string;
    payLaterDate?: string;
}

export interface BuyerAccountInvoiceGroupItem {
    amount?: Money;
    canRevoke?: boolean;
    dueDate?: string;
    dueIn?: number;
    groupId?: string;
    groupKey?: string;
    invoiceDate?: string;
    invoiceNumber?: string;
    invoiceTerms?: number;
    itemAmount?: Money;
    itemKey?: string;
    status?:
        | 'INELIGIBLE'
        | 'CREDIT_MEMO'
        | 'CLOSED'
        | 'EARLY_PAID'
        | 'EARLY_PAID_CLOSED'
        | 'ELIGIBLE'
        | 'PENDING'
        | 'REVOKED'
        | 'UNKNOWN_STATUS';
}

export interface BuyerAccountInvoiceGroupDetail {
    businessUnitName?: string;
    canRevoke?: boolean;
    credits?: Money;
    dueDate?: string;
    feeRate?: number;
    fees?: Money;
    groupId?: string;
    groupKey?: string;
    invoiceDate?: string;
    invoiceNumber?: string;
    invoiceStatus?:
        | 'CLOSED'
        | 'EARLY_PAID'
        | 'EARLY_PAID_CLOSED'
        | 'ELIGIBLE'
        | 'INELIGIBLE'
        | 'PENDING'
        | 'REVOKED'
        | 'UNKNOWN_STATUS'
        | 'UNRECOGNIZED';
    items?: BuyerAccountInvoiceGroupItem[];
    paymentDate?: string;
    paymentTotal?: Money;
    sellerName?: string;
    total?: Money;
    totalInvoiceAmount?: Money;
}

export interface SupplierPendingGroupItem {
    dueDate?: string;
    dueIn?: number;
    invoiceAmount?: Money;
    invoiceDate?: string;
    invoiceNumber?: string;
    itemKey?: string;
    status?: 'INELIGIBLE' | 'CREDIT_MEMO' | string;
    isGroup?: boolean;
}

export interface PendingGroup {
    credits?: Money;
    groupId?: string;
    groupKey?: string;
    items?: SupplierPendingGroupItem[];
    total?: Money;
    totalInvoiceAmount?: Money;
}

export interface SupplierDetail {
    sellerKey?: string;
    openInvoices?: number;
    usedPercentage?: number;
    avgDaysPaidEarly?: number;
    status?: 'UNKNOWN' | 'ENROLLED' | 'INELIGIBLE' | 'NOT_ENROLLED';
    enrollmentDate?: Date;
    defaultBankAccountKey?: string;
    isBuyerPaidEarlyPaymentFeePercentageVisible?: boolean;
    buyerPaidEarlyPaymentFeePercentage?: number;
}

export interface SupplierDetailResponse {
    supplierDetail: {
        supplierDetail: SupplierDetail;
    };
}
